import React from "react";
import "./visuals.css";

/**
 * Conceptual illustrations of how each system works. Decorative (aria-hidden):
 * the card/case-file text carries the same information. They contain no
 * measurements, only structure. Animation is CSS-only and disabled under
 * prefers-reduced-motion (see App.css).
 */

const Svg = ({ children, ...rest }) => (
  <svg className="art" viewBox="0 0 320 160" aria-hidden="true" focusable="false" {...rest}>
    {children}
  </svg>
);

/** A path with a bright dash travelling along it. */
const Pulse = ({ d, delay = 0, duration = 3 }) => (
  <>
    <path d={d} className="a-line" />
    <path
      d={d}
      pathLength="100"
      className="a-pulse"
      style={{ animationDelay: `${delay}s`, animationDuration: `${duration}s` }}
    />
  </>
);

/* Drifting Oracle: a distribution slides away from its baseline, PSI trips, retrain. */
const DriftArt = () => {
  const curve = "M24 132 C 64 132, 84 34, 120 34 C 156 34, 176 132, 216 132 Z";
  return (
    <Svg>
      <line x1="16" y1="132" x2="304" y2="132" className="a-line" />
      <path d={curve} className="a-curve a-curve--base" />
      <g className="a-drift">
        <path d={curve} className="a-curve a-curve--acc" />
      </g>
      <line x1="120" y1="22" x2="120" y2="132" className="a-dash" />
      <text x="124" y="22" className="a-text">baseline</text>
      <g className="a-flag">
        <rect x="164" y="2" width="152" height="20" rx="3" className="a-chip a-chip--warn" />
        <text x="240" y="15.5" textAnchor="middle" className="a-text a-text--strong">PSI ≥ 0.25 → retrain</text>
      </g>
    </Svg>
  );
};

/* CUDANet: many threads → one shared buffer → one optimizer update per batch. */
const CudaArt = () => (
  <Svg>
    {Array.from({ length: 30 }, (_, n) => {
      const col = n % 10;
      const row = Math.floor(n / 10);
      return (
        <circle
          key={n}
          cx={44 + col * 24}
          cy={22 + row * 16}
          r="4"
          className="a-dot"
          style={{ animationDelay: `${(col + row * 3) * 0.14}s` }}
        />
      );
    })}
    {Array.from({ length: 10 }, (_, col) => (
      <line key={col} x1={44 + col * 24} y1="58" x2={44 + col * 24} y2="82" className="a-line" />
    ))}
    <rect x="30" y="82" width="260" height="16" rx="4" className="a-box" />
    <rect x="30" y="82" width="260" height="16" rx="4" className="a-fill a-buffer" />
    <text x="30" y="116" className="a-text">threads → atomicAdd</text>
    <Pulse d="M160 98 L160 122" delay={1.6} duration={2.4} />
    <rect x="90" y="122" width="140" height="24" rx="5" className="a-chip" />
    <text x="160" y="138" textAnchor="middle" className="a-text a-text--strong">1 update / batch</text>
  </Svg>
);

/* Grain: oriented filter responses; direction spread becomes the regularity score. */
const GrainArt = () => {
  const cells = [];
  for (let row = 0; row < 6; row += 1) {
    for (let col = 0; col < 14; col += 1) {
      const angle = 24 + 14 * Math.sin(col * 1.7 + row * 2.3);
      cells.push(
        <g key={`${row}-${col}`} transform={`translate(${24 + col * 21} ${20 + row * 18}) rotate(${angle.toFixed(1)})`}>
          <line
            x1="-7" y1="0" x2="7" y2="0"
            className="a-grain"
            style={{ animationDelay: `${((col + row) % 7) * 0.22}s` }}
          />
        </g>
      );
    }
  }
  return (
    <Svg>
      {cells}
      <rect x="24" y="136" width="272" height="6" rx="3" className="a-box" />
      <rect x="24" y="136" width="272" height="6" rx="3" className="a-fill a-score" />
      <text x="24" y="152" className="a-text">grain regularity score</text>
    </Svg>
  );
};

/* PaySphere: payment lifecycle as a state machine with a token moving through it. */
const PayArt = () => (
  <Svg>
    <Pulse d="M78 76 L116 76" delay={0} duration={4} />
    <Pulse d="M180 76 C 202 76, 202 46, 224 46" delay={1} duration={4} />
    <Pulse d="M180 76 C 202 76, 202 106, 224 106" delay={2.4} duration={4} />
    {[
      ["CREATED", 14, 64, 64],
      ["PENDING", 116, 64, 64],
      ["CAPTURED", 224, 34, 82],
      ["FAILED", 224, 94, 82],
    ].map(([label, x, y, w], i) => (
      <g key={label}>
        <rect x={x} y={y} width={w} height="24" rx="6" className="a-chip a-chip--seq" style={{ animationDelay: `${i}s` }} />
        <text x={x + w / 2} y={y + 16} textAnchor="middle" className="a-text a-text--strong">{label}</text>
      </g>
    ))}
    <rect x="14" y="20" width="108" height="20" rx="5" className="a-box a-box--dash" />
    <text x="68" y="34" textAnchor="middle" className="a-text">Idempotency-Key</text>
    <rect x="52" y="124" width="216" height="20" rx="5" className="a-box a-box--dash" />
    <text x="160" y="138" textAnchor="middle" className="a-text">duplicate webhook → deduped</text>
  </Svg>
);

/* Resolvr: review feedback flows through one approval gate before anything is written back. */
const ResolvrArt = () => (
  <Svg>
    <Pulse d="M92 52 L132 52" delay={0} duration={4.5} />
    <Pulse d="M188 52 L232 52" delay={1.6} duration={4.5} />
    <Pulse d="M160 76 L160 108" delay={3} duration={4.5} />
    {[
      ["PR review", 14, 38, 78],
      ["Agent", 232, 38, 74],
    ].map(([label, x, y, w], i) => (
      <g key={label}>
        <rect x={x} y={y} width={w} height="28" rx="6" className="a-chip" />
        <text x={x + w / 2} y={y + 18} textAnchor="middle" className="a-text a-text--strong">{label}</text>
      </g>
    ))}
    <rect x="127" y="34" width="66" height="36" rx="6" className="a-chip a-chip--seq" />
    <text x="160" y="56" textAnchor="middle" className="a-text a-text--strong">Resolvr</text>
    <rect x="96" y="108" width="128" height="26" rx="13" className="a-box a-box--dash a-gate" />
    <text x="160" y="125" textAnchor="middle" className="a-text a-text--strong">human approval</text>
    <text x="160" y="152" textAnchor="middle" className="a-text">commit + push → threads resolved</text>
  </Svg>
);

/* PP-HFM: each site fuses two modalities locally; only a privacy-bounded update climbs edge → cloud,
   and the global model comes back down. Raw records never leave the dashed site boundary. */
const PpHfmArt = () => {
  const sites = [24, 68, 112];
  return (
    <Svg>
      {sites.map((cy, i) => (
        <g key={cy}>
          <rect x="8" y={cy - 18} width="76" height="36" rx="6" className="a-box a-box--dash" />
          <circle cx="22" cy={cy - 8} r="4.5" className="a-node" />
          <circle cx="22" cy={cy + 8} r="4.5" className="a-node" />
          <line x1="27" y1={cy - 7} x2="43" y2={cy - 1} className="a-line" />
          <line x1="27" y1={cy + 7} x2="43" y2={cy + 1} className="a-line" />
          <circle cx="50" cy={cy} r="7" className="a-node a-node--hub" />
          <Pulse d={`M57 ${cy} C 96 ${cy}, 104 68, 128 68`} delay={i * 0.9} duration={4.2} />
        </g>
      ))}

      <rect x="128" y="48" width="50" height="40" rx="6" className="a-chip" />
      <text x="153" y="72" textAnchor="middle" className="a-text a-text--strong">edge</text>

      <Pulse d="M178 68 L262 68" delay={1.4} duration={4.2} />
      <rect x="262" y="48" width="50" height="40" rx="6" className="a-chip a-chip--seq" />
      <text x="287" y="72" textAnchor="middle" className="a-text a-text--strong">cloud</text>

      <g className="a-flag--loop">
        <rect x="194" y="58" width="52" height="20" rx="10" className="a-chip a-chip--warn" />
        <text x="220" y="72" textAnchor="middle" className="a-text a-text--strong">ε ≤ 1</text>
      </g>

      <path d="M287 88 V120 H153 V88" className="a-dash a-return" />
      <text x="220" y="113" textAnchor="middle" className="a-text">global model</text>

      <text x="8" y="152" className="a-text">raw records never leave the site</text>
    </Svg>
  );
};

/* Federated traffic: four intersections train locally, a hub averages their updates. */
const TrafficArt = () => {
  const spots = [[56, 40], [264, 40], [56, 120], [264, 120]];
  return (
    <Svg>
      {spots.map(([x, y], i) => (
        <g key={i}>
          <Pulse d={`M${x} ${y} L160 80`} delay={i * 0.7} duration={3.6} />
          <circle cx={x} cy={y} r="14" className="a-node" />
          <path d={`M${x - 8} ${y} H${x + 8} M${x} ${y - 8} V${y + 8}`} className="a-line" />
        </g>
      ))}
      <circle cx="160" cy="80" r="22" className="a-node a-node--hub" />
      <circle cx="160" cy="80" r="22" className="a-hub-ring" />
      <text x="160" y="84" textAnchor="middle" className="a-text a-text--strong">FedAvg</text>
      <text x="160" y="152" textAnchor="middle" className="a-text">no raw traffic data is shared</text>
    </Svg>
  );
};

/* DebtSense: a dependency graph where the hub module and a cycle stand out. */
const DebtArt = () => {
  const pts = { a: [60, 40], b: [130, 28], c: [250, 36], d: [60, 120], e: [250, 122], h: [160, 80] };
  const edges = [["a", "h"], ["b", "h"], ["c", "h"], ["d", "h"], ["e", "h"], ["a", "b"], ["d", "e"]];
  return (
    <Svg>
      {edges.map(([p, q]) => (
        <line key={p + q} x1={pts[p][0]} y1={pts[p][1]} x2={pts[q][0]} y2={pts[q][1]} className="a-line" />
      ))}
      <path d="M250 36 L250 122 L160 80 Z" className="a-cycle" fill="none" />
      {Object.entries(pts).map(([k, [x, y]]) => (
        <circle key={k} cx={x} cy={y} r={k === "h" ? 16 : 9} className={k === "h" ? "a-node a-node--hub" : "a-node"} />
      ))}
      <circle cx="160" cy="80" r="16" className="a-hub-ring" />
      <text x="160" y="106" textAnchor="middle" className="a-text a-text--strong">hub module</text>
      <text x="14" y="152" className="a-text">every score shows its evidence</text>
    </Svg>
  );
};

/* digipin: a coordinate narrows to one grid cell, then to a short code. */
const DigipinArt = () => {
  const cells = [];
  for (let r = 0; r < 4; r += 1) {
    for (let c = 0; c < 8; c += 1) {
      const hit = r === 2 && c === 5;
      cells.push(
        <rect
          key={`${r}-${c}`}
          x={20 + c * 22}
          y={16 + r * 22}
          width="20"
          height="20"
          rx="3"
          className={hit ? "a-chip a-chip--seq" : "a-box a-cell"}
          style={hit ? undefined : { animationDelay: `${((r * 8 + c) % 9) * 0.2}s` }}
        />
      );
    }
  }
  return (
    <Svg>
      {cells}
      <Pulse d="M152 82 C 200 82, 214 60, 236 60" delay={0.4} duration={3.6} />
      <rect x="232" y="46" width="76" height="28" rx="6" className="a-chip" />
      <text x="270" y="64" textAnchor="middle" className="a-text a-text--strong">39J49LL8T4</text>
      <text x="20" y="134" className="a-text">~4 m × 4 m cell · no API required</text>
    </Svg>
  );
};

/* The Dropout Signal: medallion layers feed a scored table with SHAP risk factors. */
const DropoutArt = () => (
  <Svg>
    <Pulse d="M78 54 L100 54" delay={0} duration={3.6} />
    <Pulse d="M164 54 L186 54" delay={1.2} duration={3.6} />
    {[
      ["Bronze", 14],
      ["Silver", 100],
      ["Gold", 186],
    ].map(([label, x], i) => (
      <g key={label}>
        <rect x={x} y="38" width="64" height="32" rx="6" className="a-chip a-chip--seq" style={{ animationDelay: `${i * 1.2}s` }} />
        <text x={x + 32} y="58" textAnchor="middle" className="a-text a-text--strong">{label}</text>
      </g>
    ))}
    {[0, 1, 2].map((i) => (
      <rect key={i} x="186" y={88 + i * 12} width={[64, 46, 30][i] + 0} height="7" rx="3" className="a-fill a-fill--shap" style={{ animationDelay: `${i * 0.3}s` }} />
    ))}
    <text x="262" y="112" className="a-text">SHAP</text>
    <text x="14" y="152" className="a-text">risk tier + top 3 reasons</text>
  </Svg>
);

/* The CUDAnet paper in one picture: the same threads, two ways to update shared optimizer state. */
const PaperArtNarrow = () => {
  const rows = [
    { cy: 54, label: "naive · one thread per sample", mid: "shared state", end: "race", badge: "≈18%", tone: "warn" },
    { cy: 152, label: "two-phase · accumulate, then update", mid: "buffer", end: "1 update", badge: "85.64%", tone: "ok" },
  ];
  return (
    <svg className="art art--narrow" viewBox="0 0 340 196" aria-hidden="true" focusable="false">
      {rows.map((r, k) => (
        <g key={r.label}>
          <text x="10" y={r.cy - 36} className="a-text">{r.label}</text>

          {[0, 1, 2, 3].flatMap((i) =>
            [-8, 8].map((dy) => (
              <circle key={`${i}${dy}`} cx={20 + i * 16} cy={r.cy + dy} r="4" className="a-node" />
            ))
          )}

          {[-8, 0, 8].map((dy, i) => (
            <Pulse
              key={dy}
              d={`M68 ${r.cy + dy} C 84 ${r.cy + dy}, 90 ${r.cy}, 106 ${r.cy}`}
              delay={k === 0 ? i * 0.12 : i * 0.5}
              duration={3.6}
            />
          ))}

          <rect x="106" y={r.cy - 17} width="84" height="34" rx="6" className={k === 0 ? "a-chip a-chip--warn a-shake" : "a-chip"} />
          <text x="148" y={r.cy + 4} textAnchor="middle" className="a-text a-text--strong">{r.mid}</text>

          <Pulse d={`M190 ${r.cy} L204 ${r.cy}`} delay={k === 0 ? 0.9 : 1.8} duration={3.6} />
          <rect x="204" y={r.cy - 14} width="58" height="28" rx="14" className={k === 0 ? "a-box a-box--dash" : "a-chip"} />
          <text x="233" y={r.cy + 4} textAnchor="middle" className="a-text a-text--strong">{r.end}</text>

          <Pulse d={`M262 ${r.cy} L274 ${r.cy}`} delay={k === 0 ? 1.4 : 2.6} duration={3.6} />
          <rect
            x="274"
            y={r.cy - 15}
            width="58"
            height="30"
            rx="6"
            className={r.tone === "ok" ? "a-chip a-chip--seq" : "a-chip a-chip--warn"}
          />
          <text x="303" y={r.cy + 4} textAnchor="middle" className="a-text a-text--strong">{r.badge}</text>
        </g>
      ))}
      <line x1="10" y1="98" x2="330" y2="98" className="a-dash" />
    </svg>
  );
};

/* The CUDAnet paper, stretched wide: the same threads, two ways to update shared optimizer state. */
const PaperArt = () => {
  const rows = [
    { cy: 48, label: "naive · one thread per sample", mid: "shared state", end: "race", badge: "≈18%", ok: false },
    { cy: 128, label: "two-phase · accumulate, then update", mid: "buffer", end: "1 update", badge: "85.64%", ok: true },
  ];
  return (
    <svg className="art art--wide" viewBox="0 0 520 168" aria-hidden="true" focusable="false">
      {rows.map((r, k) => (
        <g key={r.label}>
          <text x="10" y={r.cy - 30} className="a-text">{r.label}</text>

          {[0, 1, 2, 3].flatMap((i) =>
            [-8, 8].map((dy) => (
              <circle key={`${i}${dy}`} cx={20 + i * 17} cy={r.cy + dy} r="4.2" className="a-node" />
            ))
          )}

          {[-8, 0, 8].map((dy, i) => (
            <Pulse
              key={dy}
              d={`M71 ${r.cy + dy} C 130 ${r.cy + dy}, 150 ${r.cy}, 206 ${r.cy}`}
              delay={k === 0 ? i * 0.12 : i * 0.5}
              duration={3.8}
            />
          ))}

          <rect x="206" y={r.cy - 18} width="104" height="36" rx="6" className={k === 0 ? "a-chip a-chip--warn a-shake" : "a-chip"} />
          <text x="258" y={r.cy + 4} textAnchor="middle" className="a-text a-text--strong">{r.mid}</text>

          <Pulse d={`M310 ${r.cy} L346 ${r.cy}`} delay={k === 0 ? 0.9 : 1.8} duration={3.8} />
          <rect x="346" y={r.cy - 15} width="70" height="30" rx="15" className={k === 0 ? "a-box a-box--dash" : "a-chip"} />
          <text x="381" y={r.cy + 4} textAnchor="middle" className="a-text a-text--strong">{r.end}</text>

          <Pulse d={`M416 ${r.cy} L446 ${r.cy}`} delay={k === 0 ? 1.4 : 2.6} duration={3.8} />
          <rect x="446" y={r.cy - 16} width="66" height="32" rx="6" className={r.ok ? "a-chip a-chip--seq" : "a-chip a-chip--warn"} />
          <text x="479" y={r.cy + 4} textAnchor="middle" className="a-text a-text--strong">{r.badge}</text>
        </g>
      ))}
      <line x1="10" y1="86" x2="510" y2="86" className="a-dash" />
    </svg>
  );
};

const ART = {
  "drifting-oracle": DriftArt,
  cudanet: CudaArt,
  "grain-regularity": GrainArt,
  paysphere: PayArt,
  resolvr: ResolvrArt,
  "cudanet-paper": PaperArt,
  "cudanet-paper-narrow": PaperArtNarrow,
  "pp-hfm": PpHfmArt,
  "federated-traffic": TrafficArt,
  debtsense: DebtArt,
  digipin: DigipinArt,
  "dropout-signal": DropoutArt,
};

const ProjectArt = ({ slug }) => {
  const Art = ART[slug];
  return Art ? <Art /> : null;
};

export default ProjectArt;
